// ── RestaurantDashboard ──────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { ShoppingBag, TrendingUp, Star, Clock } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { OrderStatusBadge, Spinner } from "../../components/common/UI";

export function RestaurantDashboard() {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [stats, setStats] = useState({ orders: 0, pending: 0, revenue: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: rest } = await supabase.from("restaurants").select("*").eq("owner_id", user.id).single();
      if (!rest) { setLoading(false); return; }
      setRestaurant(rest);

      const [{ data: orders }, { count: pending }] = await Promise.all([
        supabase.from("orders").select("*").eq("restaurant_id", rest.id).order("created_at", { ascending: false }).limit(6),
        supabase.from("orders").select("*", { count: "exact", head: true }).eq("restaurant_id", rest.id).eq("status", "pending"),
      ]);
      const revenue = (orders || []).filter(o => o.status === "delivered").reduce((s, o) => s + (o.total || 0), 0);
      setStats({ orders: orders?.length || 0, pending: pending || 0, revenue });
      setRecentOrders(orders || []);
      setLoading(false);
    };
    load();
  }, [user]);

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;
  if (!restaurant) return (
    <div className="text-center py-16">
      <p className="text-4xl mb-3">🍽️</p>
      <h2 className="font-display text-xl font-bold text-gray-700">No restaurant assigned yet</h2>
      <p className="text-gray-500 mt-2">Please contact admin to assign your restaurant.</p>
    </div>
  );

  return (
    <div className="space-y-6 page-enter">
      <div>
        <h1 className="font-display text-2xl font-bold text-gray-900">{restaurant.name}</h1>
        <p className="text-gray-500 text-sm">Welcome back! Here's your overview.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: ShoppingBag, label: "Total Orders", value: stats.orders, color: "bg-orange-500" },
          { icon: Clock, label: "Pending", value: stats.pending, color: "bg-yellow-500" },
          { icon: TrendingUp, label: "Revenue", value: `₨${stats.revenue.toLocaleString()}`, color: "bg-green-500" },
          { icon: Star, label: "Rating", value: restaurant.rating > 0 ? restaurant.rating.toFixed(1) : "New", color: "bg-blue-500" },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center mb-3`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Recent Orders</h2>
        </div>
        {recentOrders.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No orders yet</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentOrders.map((o) => (
              <div key={o.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-sm text-gray-900">{o.customer_name}</p>
                  <p className="text-xs text-gray-400">{o.customer_phone} · ₨{o.total?.toLocaleString()}</p>
                </div>
                <OrderStatusBadge status={o.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default RestaurantDashboard;
